"use client";

import { Clock, Monitor, Moon, Stethoscope, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHealthcareTheme, useTheme } from "@/lib/theme/theme-context";
import { useAuth } from "@/lib/use-auth";
import { UserRole } from "@/types/user-roles";

interface ThemeToggleProps {
  variant?: "default" | "simple" | "healthcare";
  showHealthcareOptions?: boolean;
}

export function ThemeToggle({ variant = "default", showHealthcareOptions = true }: ThemeToggleProps) {
  // Hooks must be called before any early returns
  const { theme, setTheme, actualTheme, isHealthcareOptimized, setHealthcareOptimized } = useTheme();
  const { isNightShift, suggestThemeForShift } = useHealthcareTheme();

  // Component disabled - TODO: Remove this when ready to enable
  return null;

  // Simple toggle for basic use cases
  if (variant === "simple") {
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={() => setTheme(actualTheme === "light" ? "dark" : "light")}
        aria-label={`Switch to ${actualTheme === "light" ? "dark" : "light"} mode`}
      >
        <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        <span className="sr-only">Toggle theme</span>
      </Button>
    );
  }

  // Healthcare variant with shift awareness
  if (variant === "healthcare") {
    const currentShift = isNightShift() ? "Night Shift" : "Day Shift";
    const suggestedTheme = suggestThemeForShift();
    const isUsingSuggested = actualTheme === suggestedTheme;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Stethoscope className="h-4 w-4" />
            <Clock className="h-4 w-4" />
            <span className="text-xs font-medium">{currentShift}</span>
            {actualTheme === "light" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            Healthcare Display
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            Current: {currentShift}
            {!isUsingSuggested && (
              <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">Suggested: {suggestedTheme} mode</div>
            )}
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => setTheme(suggestedTheme)} className="gap-2">
            {suggestedTheme === "light" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            Use Suggested ({suggestedTheme})
            {isUsingSuggested && <span className="ml-auto text-green-600 dark:text-green-400">✓</span>}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuCheckboxItem checked={isHealthcareOptimized} onCheckedChange={setHealthcareOptimized}>
            Healthcare Optimization
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default dropdown variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => setTheme("light")} className="gap-2">
          <Sun className="h-4 w-4" />
          Light
          {theme === "light" && <span className="ml-auto text-green-600 dark:text-green-400">✓</span>}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => setTheme("dark")} className="gap-2">
          <Moon className="h-4 w-4" />
          Dark
          {theme === "dark" && <span className="ml-auto text-green-600 dark:text-green-400">✓</span>}
        </DropdownMenuItem>

        <DropdownMenuItem onClick={() => setTheme("system")} className="gap-2">
          <Monitor className="h-4 w-4" />
          System
          {theme === "system" && <span className="ml-auto text-green-600 dark:text-green-400">✓</span>}
        </DropdownMenuItem>

        {showHealthcareOptions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-2">
              <Stethoscope className="h-4 w-4" />
              Healthcare Options
            </DropdownMenuLabel>

            <DropdownMenuCheckboxItem checked={isHealthcareOptimized} onCheckedChange={setHealthcareOptimized}>
              Healthcare Optimization
            </DropdownMenuCheckboxItem>

            {isNightShift() && (
              <DropdownMenuItem onClick={() => setTheme("dark")} className="gap-2 text-blue-600 dark:text-blue-400">
                <Clock className="h-4 w-4" />
                Night Shift Mode
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Quick access buttons for healthcare staff
export function QuickThemeActions() {
  // Hooks must be called before any early returns
  const { setTheme, actualTheme } = useTheme();
  const { isNightShift, suggestThemeForShift: _suggestThemeForShift } = useHealthcareTheme();
  const { user } = useAuth();

  // Component disabled - TODO: Remove this when ready to enable
  return null;

  if (user?.role === UserRole.PATIENT) return null;

  if (!isNightShift()) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex gap-2">
      <Button
        size="sm"
        variant={actualTheme === "dark" ? "default" : "outline"}
        onClick={() => setTheme("dark")}
        className="gap-2"
      >
        <Moon className="h-4 w-4" />
        Night Mode
      </Button>
      <Button
        size="sm"
        variant={actualTheme === "light" ? "default" : "outline"}
        onClick={() => setTheme("light")}
        className="gap-2"
      >
        <Sun className="h-4 w-4" />
        Day Mode
      </Button>
    </div>
  );
}
